import React, { useCallback, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Play, ArrowLeft, Filter, Search, Image as ImageIcon, Crown, Check } from "lucide-react";

// Import Actions
import { getTemplateByCategoryRequest } from "../store/slice/appSlice";

export default function CategoryPage() {
  const { slug } = useParams();
  const dispatch = useDispatch();

  // Redux State
  const { 
    getTemplateByCategoryData, 
    getTemplateByCategoryLoading,
    getTemplateByCategoryError
  } = useSelector((state) => state.app);

  useEffect(() => {
    if (slug) {
      dispatch(getTemplateByCategoryRequest({ slug, page: 1 }));
    }
  }, [dispatch, slug]);

  const templates = getTemplateByCategoryData?.data || [];
  const pagination = getTemplateByCategoryData || {};
  const isInitialLoading = getTemplateByCategoryLoading && templates.length === 0;
  const isPaginating = getTemplateByCategoryLoading && templates.length > 0;

  const handlePageChange = (page) => {
    if (page === pagination.current_page || getTemplateByCategoryLoading) return;

    dispatch(getTemplateByCategoryRequest({ slug, page }));
  };

  const renderPagination = useCallback(() => {
    if (!pagination?.last_page || pagination.last_page <= 1) return null;

    const totalPages = pagination.last_page;
    const currentPage = pagination.current_page;
    const pages = [];

    if (totalPages <= 8) {
      for (let page = 1; page <= totalPages; page += 1) {
        pages.push(page);
      }
    } else {
      const startPages = [1, 2, 3].filter((page) => page <= totalPages);
      const endPages = [totalPages - 2, totalPages - 1, totalPages];
      const middleStart = Math.max(4, currentPage - 1);
      const middleEnd = Math.min(totalPages - 3, currentPage + 1);
      const middlePages = [];

      for (let page = middleStart; page <= middleEnd; page += 1) {
        middlePages.push(page);
      }

      const visiblePages = [...startPages, ...middlePages, ...endPages]
        .filter((page, index, array) => array.indexOf(page) === index)
        .sort((a, b) => a - b);

      visiblePages.forEach((page, index) => {
        pages.push(page);

        const nextPage = visiblePages[index + 1];
        if (nextPage && nextPage - page > 1) {
          pages.push("ellipsis-" + page);
        }
      });
    }

    return (
      <div className="flex justify-center gap-2 mt-10 flex-wrap">
        {pages.map((page) => {
          if (typeof page === "string") {
            return (
              <span
                key={page}
                className="flex items-center justify-center w-10 h-10 text-sm font-semibold text-gray-500"
              >
                ...
              </span>
            );
          }

          const isActive = page === pagination.current_page;

          return (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`w-10 h-10 rounded-full text-sm font-semibold transition ${
                isActive
                  ? "bg-yellow-500 text-white"
                  : "bg-white border hover:bg-gray-100"
              }`}
              disabled={getTemplateByCategoryLoading}
            >
              {page}
            </button>
          );
        })}
      </div>
    );
  }, [pagination]);

  return (
    <div className="min-h-screen px-6 py-8">
      <div className="max-w-7xl mx-auto">
        
        {/* --- HEADER SECTION --- */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 border-b border-gray-200/60 pb-6">
          <div>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-yellow-600 transition-colors mb-3 group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Back to Categories
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
              Explore Templates
            </h1>
            <p className="text-gray-500 mt-2">
              Discover professionally designed invitations for your special moments.
            </p>
          </div>

          {/* Controls (Search/Filter - Visual Only for now) */}
          {/* <div className="flex gap-3">
             <div className="relative group">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-yellow-500" />
                <input 
                  type="text" 
                  placeholder="Search in category..." 
                  className="pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition-all w-full md:w-64 shadow-sm"
                />
             </div>
             <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition shadow-sm">
               <Filter className="w-4 h-4" /> Filters
             </button>
          </div> */}
        </div>

        {/* --- LOADING STATE --- */}
        {isInitialLoading && (
           <div className="h-80 flex flex-col items-center justify-center text-gray-400 gap-4">
             <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
             <p className="font-medium animate-pulse">Curating designs for you...</p>
           </div>
        )}

        {/* --- ERROR STATE --- */}
        {getTemplateByCategoryError && (
           <div className="p-6 bg-red-50 text-red-600 rounded-xl text-center border border-red-100 max-w-lg mx-auto">
             <p className="font-semibold">Unable to load templates.</p>
             <p className="text-sm opacity-80 mt-1">{typeof getTemplateByCategoryError === 'string' ? getTemplateByCategoryError : "Please check your connection and try again."}</p>
           </div>
        )}

        {/* --- EMPTY STATE --- */}
        {!isInitialLoading && templates.length === 0 && (
           <div className="text-center py-20 bg-white/50 backdrop-blur-sm rounded-2xl border border-dashed border-gray-300">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
                <ImageIcon className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700">No templates found</h3>
              <p className="text-gray-500 text-sm mt-1">We are adding new designs soon!</p>
           </div>
        )}

        {/* --- TEMPLATE GRID --- */}
        {!isInitialLoading && (
        <>
        <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 transition-opacity ${
          isPaginating ? "opacity-60" : "opacity-100"
        }`}>
          {templates.map((template) => (
            <div 
              key={template.id} 
              className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100"
            >
               
               {/* Thumbnail (9:16 Ratio for Mobile Invites) */}
               <div className="aspect-[9/16] bg-gray-100 relative overflow-hidden">
                  {template.preview_file_url ? (
                    <img 
                      src={template.preview_file_url} 
                      alt={template.title} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 bg-gray-50">
                       <ImageIcon className="w-10 h-10 mb-2" />
                       <span className="text-xs font-medium">No Preview</span>
                    </div>
                  )}

                  {/* Gradient Overlay (Bottom) for Text readability if needed */}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  {/* Badges */}
                  <div className="absolute top-3 left-3">
  {template.is_free === 1 ? (
    <span
      className="flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-500/90 backdrop-blur-md shadow-md"
      title="Free Template"
    >
      <Check className="w-4 h-4 text-white" />
    </span>
  ) : (
    <span
      className="flex items-center justify-center w-7 h-7 rounded-lg bg-yellow-500/90 backdrop-blur-md shadow-md"
      title="Premium Template"
    >
      <Crown className="w-4 h-4 text-white" />
    </span>
  )}
</div>

                  {/* Hover Action Overlay */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                     <Link 
                       to={`/template/${template.slug}`}
                       className="bg-white text-gray-900 px-5 py-2.5 rounded-full font-bold text-sm shadow-2xl hover:bg-yellow-50 hover:scale-105 transition-all flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 duration-300"
                     >
                       <Play className="w-3.5 h-3.5 fill-current" /> Customize
                     </Link>
                  </div>
               </div>

               {/* Info Footer */}
               {/* <div className="p-4 bg-white relative z-10">
                 <h3 className="font-bold text-gray-800 text-sm mb-1 truncate leading-tight" title={template.title}>
                   {template.title}
                 </h3>
                 <div className="flex justify-between items-center text-xs text-gray-500 font-medium">
                    <span className="flex items-center gap-1">
                      {template.mime_type === 'video' ? '🎬 Video' : '🖼️ Card'}
                    </span>
                    <span className="text-yellow-600">★★★★★</span>
                 </div>
               </div> */}
               {/* Info Footer */}
              <div className="p-4 bg-white relative z-10 space-y-3">
                <h3
                  className="font-bold text-gray-800 text-sm truncate leading-tight"
                  title={template.title}
                >
                  {template.title}
                </h3>

                {/* <p className="text-xs text-gray-400 font-mono -mt-2">
                  ID: {template.id}
                </p> */}

                <div className="flex justify-between items-center text-xs text-gray-500 font-medium">
                  {/* <span>
                    {template.mime_type === "video" ? "🎬 Video Invite" : "🖼️ Card Invite"}
                  </span> */}
                  <span>
                    ID: T{template.id}
                  </span>
                  {template.is_free === 1 ? (
                    <span className="text-green-600 font-semibold">Free</span>
                  ) : (
                    <span className="text-yellow-600 font-semibold">Premium</span>
                  )}
                </div>

                {/* Always Visible Button */}
                <Link
                  to={`/template/${template.slug}`}
                  className="block w-full text-center bg-yellow-500 hover:bg-yellow-600 text-white font-semibold text-sm py-2 rounded-lg transition"
                >
                  Customize
                </Link>
              </div>
            </div>
          ))}
        </div>

        {isPaginating && (
          <div className="flex items-center justify-center gap-3 mt-6 text-sm text-gray-500">
            <div className="w-5 h-5 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
            <span>Loading next page...</span>
          </div>
        )}

        {renderPagination()}
        </>
        )}
      </div>
    </div>
  );
}
